import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Row, Col, Button as AntdButton } from "antd";
import { Button } from "components/Buttons/Buttons";
import Modal from "components/Modal/Modal";
import {
    FormInput,
    FormInputArea,
    FormInputNumber,
    FormSelect,
    FormSwitch,
    FormHiddenSubmitButton,
} from "components/FormItems/FlatFormItems";
import { useForm } from "antd/lib/form/Form";
import RewardImageUpload from "./RewardImageUpload";
import { useDispatch, useSelector } from "react-redux";
import { BENEFIT_TYPE, EXCLUSION_TYPE } from "utils/enums";
import {
    createLoyaltyRewardCatalogItem,
    getLoyaltyRewardCatalogItem,
    updateLoyaltyRewardCatalogItem,
} from "services/loyaltyRewardCatalog.service";

const benefitTypes = Object.entries(BENEFIT_TYPE)
    .filter(([label]) => label !== "REFERAL")
    .map(([label, value]) => ({
        label,
        value,
    }));

const exclusionTypes = Object.entries(EXCLUSION_TYPE).map(([label, value]) => ({
    label,
    value,
}));

export default function LoyaltyRewardCatalogAddEditModal({
    products,
    isProductsLoading,
    categories,
    isCategoriesLoading,
    data = {},
    visible,
    onCancel,
    onSuccess,
}) {
    const [form] = useForm();
    const submitRef = useRef();
    const dispatch = useDispatch();

    const [base64, setBase64] = useState("");
    const [file, setFile] = useState(null);

    const { allFacilities, isFetchingFacilities } = useSelector(
        (state) => state.facilities
    );

    const benefitType = Form.useWatch("benefitType", form);
    const benefitValueApplicable =
        benefitType === BENEFIT_TYPE["Discount Amount"] ||
        benefitType === BENEFIT_TYPE["Discount Percentage"];

    const productIds = Form.useWatch("productIds", form);
    const categoryIds = Form.useWatch("categoryIds", form);
    const productExcludeType = Form.useWatch("productExcludeType", form);
    const facilityExcludeType = Form.useWatch("facilityExcludeType", form);
    const categoryExcludeType = Form.useWatch("categoryExcludeType", form);

    const isEdit = data?.loyaltyRewardCatalogItemId !== undefined;

    const loadData = () => {
        dispatch(getLoyaltyRewardCatalogItem(data?.loyaltyRewardCatalogItemId)).then(
            (res) => {
                if (res?.status === "success") {
                    form.setFieldsValue({ ...res?.data });
                    setBase64(res?.data?.imageUrl);
                }
            }
        );
    };

    useEffect(() => {
        if (visible && isEdit) {
            form.setFieldsValue({ ...data });
            setBase64(data?.imageUrl);
            loadData();
        } else {
            form.resetFields();
            setBase64("");
            setFile(null);
        }
    }, [visible]);

    const handleSubmit = useCallback(
        async (values) => {
            const submittableValues = {
                ...values,
                benefitValue: benefitValueApplicable ? values.benefitValue : null,
                imageBase64String: base64?.split?.("base64,")?.[1],
                imageExt: file?.type?.split("/")[1],
            };

            let result = null;
            if (isEdit) {
                result = await dispatch(
                    updateLoyaltyRewardCatalogItem(
                        data.loyaltyRewardCatalogItemId,
                        submittableValues
                    )
                );
            } else {
                result = await dispatch(createLoyaltyRewardCatalogItem(submittableValues));
            }

            if (result?.status === "success") {
                onSuccess?.(submittableValues);
            }
        },
        [data, isEdit, onSuccess, base64, file, benefitValueApplicable]
    );

    const filteredProducts = React.useMemo(() => {
        let productsByCategory = [];
        if (categoryExcludeType === EXCLUSION_TYPE["All Included"]) {
            productsByCategory = products;
        } else if (categoryExcludeType === EXCLUSION_TYPE["All Include Except"]) {
            productsByCategory = products.filter((product) => {
                return !product.productCategoryIds?.some((categoryId) =>
                    categoryIds?.includes(categoryId)
                );
            });
        } else if (categoryExcludeType === EXCLUSION_TYPE["Only Included"]) {
            productsByCategory = products.filter((product) => {
                return product.productCategoryIds?.find((categoryId) =>
                    categoryIds?.includes(categoryId)
                );
            });
        }

        return (
            productsByCategory?.filter(
                (it) => it?.isActive || productIds?.includes?.(it?.productId)
            ) || []
        );
    }, [products, categoryExcludeType, categoryIds, productIds]);

    return (
        <Modal
            className="ant-modal-title-tradeGothic"
            destroyOnClose={true}
            width="720px"
            title={isEdit ? "Edit Rewards/Redemption" : "Create Rewards/Redemption"}
            onCancel={() => onCancel()}
            visible={visible}
            centered={true}
            footer={
                <div className="d-flex justify-between align-center">
                    <AntdButton type="text" onClick={() => onCancel()} style={{ padding: 0 }}>
                        Cancel
                    </AntdButton>
                    <Button
                        style={{ height: 50 }}
                        rounded={true}
                        onClick={() => submitRef.current?.click?.()}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <Form
                form={form}
                initialValues={{
                    isActive: true,
                    facilityExcludeType: EXCLUSION_TYPE["All Included"],
                    categoryExcludeType: EXCLUSION_TYPE["All Included"],
                    productExcludeType: EXCLUSION_TYPE["All Included"],
                }}
                layout="vertical"
                requiredMark={false}
                preserve={false}
                onFinish={handleSubmit}
                onFinishFailed={({ errorFields }) => {
                    form.scrollToField(errorFields[0].name, {
                        scrollMode: "if-needed",
                        block: "center",
                        behavior: "smooth",
                    });
                }}
            >
                <Row gutter={[24, 16]}>
                    <Col xs={24}>
                        <FormInput type="hidden" name="imageUrl" formItemStyles={{height: 1, margin: 0}} />
                        {visible && (
                            <RewardImageUpload
                                isEdit={isEdit}
                                base64={base64}
                                setBase64={setBase64}
                                setFile={setFile}
                            />
                        )}
                    </Col>
                    <Col xs={24}>
                        <FormInput
                            variant="underlined"
                            name="name"
                            label="Name"
                            placeholder="Reward Name"
                            required
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24}>
                        <FormInputArea
                            variant="underlined"
                            name="description"
                            label="Description"
                            placeholder="Description"
                            required
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormInputNumber
                            name="pointsCost"
                            variant="underlined"
                            label="Points Cost"
                            placeholder="Points Cost"
                            min={0.01}
                            required
                            rules={[
                                {
                                    message: "Must be greater than 0",
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value <= 0) {
                                            return Promise.reject();
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            name="benefitType"
                            variant="underlined"
                            label="Benefit Type"
                            placeholder="Select Benefit Type"
                            options={benefitTypes}
                            renderLabel="label"
                            renderValue="value"
                            required
                            onChange={() => {
                                form.setFieldsValue({ benefitValue: undefined });
                            }}
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    {benefitValueApplicable && (
                        <Col xs={24} md={12}>
                            <FormInputNumber
                                name="benefitValue"
                                variant="underlined"
                                label={
                                    benefitType === BENEFIT_TYPE["Discount Percentage"]
                                        ? "Benefit Value (%)"
                                        : "Benefit Value ($)"
                                }
                                placeholder="Benefit Value"
                                min={0}
                                max={
                                    benefitType === BENEFIT_TYPE["Discount Percentage"]
                                        ? 100
                                        : undefined
                                }
                                required
                                formItemStyles={{ marginBottom: 0 }}
                            />
                        </Col>
                    )}
                    <Col xs={24} md={12}>
                        <FormSelect
                            variant="underlined"
                            name="facilityExcludeType"
                            label="Facility Exclusion Type"
                            placeholder="Select exclusion type"
                            formItemStyles={{ marginBottom: 0 }}
                            options={exclusionTypes}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            renderLabel="label"
                            renderValue="value"
                            required
                            onChange={() => {
                                form.setFieldsValue({ facilityIds: [] });
                            }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            mode="multiple"
                            variant="underlined"
                            name="facilityIds"
                            loading={isFetchingFacilities}
                            label="Facilities"
                            renderValue="facilityId"
                            placeholder="Select Facilities"
                            renderLabel="facilityName"
                            formItemStyles={{ marginBottom: 0 }}
                            options={allFacilities?.filter((it) => it?.isActive)}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            disabled={facilityExcludeType === EXCLUSION_TYPE["All Included"]}
                            required={facilityExcludeType !== EXCLUSION_TYPE["All Included"]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            variant="underlined"
                            name="categoryExcludeType"
                            label="Category Exclusion Type"
                            placeholder="Select exclusion type"
                            formItemStyles={{ marginBottom: 0 }}
                            options={exclusionTypes}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            renderLabel="label"
                            renderValue="value"
                            required
                            onChange={() => {
                                form.setFieldsValue({ categoryIds: [], productIds: [] });
                            }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            variant="underlined"
                            name="categoryIds"
                            label="Categories"
                            placeholder="Select categories"
                            mode="multiple"
                            formItemStyles={{ marginBottom: 0 }}
                            options={categories?.filter(
                                (it) => it?.isActive || categoryIds?.includes?.(it?.categoryId)
                            )}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            renderLabel="categoryName"
                            renderValue="categoryId"
                            disabled={categoryExcludeType === EXCLUSION_TYPE["All Included"]}
                            required={categoryExcludeType !== EXCLUSION_TYPE["All Included"]}
                            loading={isCategoriesLoading}
                            onChange={() => {
                                form.setFieldsValue({ productIds: [] });
                            }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            variant="underlined"
                            name="productExcludeType"
                            label="Product Exclusion Type"
                            placeholder="Select exclusion type"
                            formItemStyles={{ marginBottom: 0 }}
                            options={exclusionTypes}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            renderLabel="label"
                            renderValue="value"
                            required
                            onChange={() => {
                                form.setFieldsValue({ productIds: [] });
                            }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            variant="underlined"
                            name="productIds"
                            label="Products"
                            placeholder="Select products"
                            mode="multiple"
                            formItemStyles={{ marginBottom: 0 }}
                            options={filteredProducts}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            renderLabel="productName"
                            renderValue="productId"
                            disabled={productExcludeType === EXCLUSION_TYPE["All Included"]}
                            required={productExcludeType !== EXCLUSION_TYPE["All Included"]}
                            loading={isProductsLoading}
                        />
                    </Col>
                    <Col xs={24}>
                        <FormSwitch name="isActive" label="Active" />
                    </Col>
                </Row>
                <FormHiddenSubmitButton submitRef={submitRef} />
            </Form>
        </Modal>
    );
}
